// Single source of truth for the Bahadura "8 (now 9) new plot charges"
// Demand Note - Site Plan calculation (see the vault plan this was built
// from: "8 Plot Charges - Implementation Plan.md" and "ALL CHARGES.pdf",
// section "1. REGULARISATION OF PLOT"). Previously this arithmetic was
// copy-pasted independently into LevelOneApplicationViewDocsAndConfirm.tsx,
// LevelTwoApplicationViewDocsAndConfirm.tsx and LevelThreeapplicationDoc.tsx
// -- every one of L1/L2/L3's review screens must import and call this
// instead of re-deriving the formulas, so a rate/formula fix (like the
// Infrastructure Fee max-of-two fix below) or a new charge (Fire Charges)
// only has to be written once.

export type TNewPlotChargeRates = {
  rrLandRate: number;
  rrLandRateCommercial: number;
  rrLandRateIndustrial: number;
  rrLandRateMixed: number;
  scrutinyRate: number;
  developmentRateResidential: number;
  developmentRateIndustrial: number;
  developmentRateCommercial: number;
  developmentRateMixed: number;
  compoundingMultiplier: number;
  supervisionRate: number;
  swmRate: number;
  administrativeRate: number;
  surveyMeasurementFee: number;
  infrastructureFeePercent: number;
  // "150 X PLOT AREA" per ALL CHARGES.pdf item 8 -- the flat half of the
  // Infrastructure Fee "max. of this" comparison. Config-driven like every
  // other rate here rather than hardcoded, in case the norm value changes.
  infrastructureFeeFlatRate: number;
};

export function round2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export function getDevelopmentRateForUsageType(
  plotUsageType: string | undefined,
  rates: TNewPlotChargeRates
): number {
  if (plotUsageType === "Industrial") return rates.developmentRateIndustrial;
  if (plotUsageType === "Commercial") return rates.developmentRateCommercial;
  if (plotUsageType === "Mixed") return rates.developmentRateMixed;
  return rates.developmentRateResidential;
}

// R3 Zone is hardcoded to Mouja "Gonhi" per the confirmed vault plan --
// "not a config value" (Karan's explicit call, "for now").
export function isR3ZoneMauja(mauja: unknown): boolean {
  return String(mauja || "").trim().toLowerCase() === "gonhi";
}

export type TComputedNewPlotCharges = {
  scrutinyCharges: number;
  developmentRate: number;
  developmentTotal: number; // particular2Total
  compoundingCharges: number;
  supervisionCharges: number;
  swmCharges: number;
  administrativeCharges: number;
  surveyMeasurementCharges: number;
  infrastructureFee: number;
};

/**
 * Pure calculation, no React/form dependency -- easy to reuse from any of
 * L1/L2/L3's review screens and to unit-test independently of form state.
 *
 * TEMPORARY (per explicit instruction, to be revisited/formalized later):
 * Scrutiny Charges is a flat rates.scrutinyRate (500) per application,
 * regardless of area -- NOT the (areaOfPlot/4000) * scrutinyRate formula
 * from the original vault plan, which produced near-zero charges for small
 * plots (e.g. 8.13 for a 65 sqm plot) that didn't match the intended
 * business rule of a flat charge.
 *
 * NOTE: the R3-zone override-carry-forward gap (a reviewer's manual
 * override of Infrastructure Fee getting silently recomputed away on the
 * next level's screen) is a known, separate issue, deferred on request --
 * not fixed by this function.
 */
export function computeNewPlotCharges({
  areaOfPlot,
  plotUsageType,
  mauja,
  asrMaxRate,
  rates,
}: {
  areaOfPlot: number;
  plotUsageType: string | undefined;
  mauja: unknown;
  asrMaxRate: number;
  rates: TNewPlotChargeRates;
}): TComputedNewPlotCharges {
  const area = Number(areaOfPlot) || 0;
  const asr = Number(asrMaxRate) || 0;
  const developmentRate = getDevelopmentRateForUsageType(plotUsageType, rates);

  // TEMPORARY: flat per-application charge, not area-scaled -- see note above.
  const scrutinyCharges = round2(rates.scrutinyRate);
  const developmentTotal = round2((developmentRate / 100) * asr * area);
  const compoundingCharges = round2(rates.compoundingMultiplier * developmentTotal);
  const supervisionCharges = round2(rates.supervisionRate * area);
  const swmCharges = round2(rates.swmRate * area);
  const administrativeCharges = round2(rates.administrativeRate * area);
  const surveyMeasurementCharges = round2(rates.surveyMeasurementFee);

  // ALL CHARGES.pdf item 8: Infrastructure Fee (For R3 Zone only) is the
  // MAX of "150 X PLOT AREA" and "15% X ASR X PLOT AREA" -- previously only
  // the percent half was computed.
  const infrastructureFee = isR3ZoneMauja(mauja)
    ? round2(
        Math.max(
          rates.infrastructureFeeFlatRate * area,
          (rates.infrastructureFeePercent / 100) * asr * area
        )
      )
    : 0;

  return {
    scrutinyCharges,
    developmentRate,
    developmentTotal,
    compoundingCharges,
    supervisionCharges,
    swmCharges,
    administrativeCharges,
    surveyMeasurementCharges,
    infrastructureFee,
  };
}

/**
 * Total Amount rollup for the new-plot-charges Demand Note, including Fire
 * Charges -- a manual entry (ALL CHARGES.pdf item 9 gives no formula, "as
 * per norms"), so it's never auto-computed, just summed in like the other
 * 8. Call this with whichever of computeNewPlotCharges's fields are
 * actually in effect for the caller (some may be reviewer-overridden and
 * read from form state instead of the computed value) plus the current
 * fireCharges value.
 */
export function sumNewPlotCharges(charges: {
  scrutinyCharges: number;
  developmentTotal: number;
  compoundingCharges: number;
  supervisionCharges: number;
  swmCharges: number;
  administrativeCharges: number;
  surveyMeasurementCharges: number;
  infrastructureFee: number;
  fireCharges: number;
  customChargeAmount?: number;
}): number {
  /**
   * The Demand Note total is charged in whole rupees, so the sum is rounded
   * to the nearest rupee (.50 and above rounds up). The individual charges
   * above keep their two decimal places; only the total is rounded.
   */
  // return round2(
  return Math.round(
    charges.scrutinyCharges +
      charges.developmentTotal +
      charges.compoundingCharges +
      charges.supervisionCharges +
      charges.swmCharges +
      charges.administrativeCharges +
      charges.surveyMeasurementCharges +
      charges.infrastructureFee +
      charges.fireCharges +
      (Number(charges.customChargeAmount) || 0)
  );
}
